import React, { useState } from "react";
import { injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ScenarioBar from "../../components/ScenarioBar";
import { ROUTES, SCENARIOS, QUIZZES } from "../../utils/constants";
import { getScenarioNumByPath, getTimer } from "../../utils/helpers";
import { updateTime } from "../../redux/actions";
import { postAnswer } from "../../api/answer";
import { modalConfirm, modalQuestionResolution, modalHint, modalHintShow } from "../../utils/logger";
import { buildQuizFeedbackBlockHtml } from "../../utils/quizFeedbackHtml";
import CheckButton from "../../components/CheckButton";
import estasseguro from "../../assets/bg/estasseguro-avatar-blue.png";
import respuestacorrecta from "../../assets/bg/correcto-avatar.png";
import respuestaincorrecta from "../../assets/bg/incorrecto-avatar.png";
import btnPistaBlue from "../../assets/btnPistaBlue.png";
import imageHint from "../../assets/hints/imageHintBlue.png";
import imageHintUsed from "../../assets/btnPistaBlue.png";
import imgPistaQuiz1 from "../../assets/hints/hint-pregunta11.png";
import unIntento from "../../assets/un-intento-blue.png";
import dosIntentos from "../../assets/dos-intentos-blue.png";

const BLUE = "#69D0FF";
const CORRECT_NUMERIC = 8.4;

/** Acepta 8.4, 8,4, 8.40, 8,40, espacios, etc. */
function parseDecimalAnswer(raw) {
	if (raw == null) return null;
	let s = String(raw).trim().replace(/\s/g, "");
	if (s === "") return null;
	s = s.replace(",", ".");
	const n = parseFloat(s);
	if (!Number.isFinite(n)) return null;
	return n;
}

function isCorrectPercentAnswer(raw) {
	const n = parseDecimalAnswer(raw);
	if (n === null) return false;
	return Math.abs(n - CORRECT_NUMERIC) < 1e-6;
}

function Scenario4Quiz5() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();
	const group = useSelector((state) => state.group);

	const activeScenario = getScenarioNumByPath(location.pathname);

	const QUIZ_ID = QUIZZES.QUIZ11;
	const SCENARIO_ID = SCENARIOS.SCENARIO_4;

	const [value, setValue] = useState("");
	const [isCompleted, setIsCompleted] = useState(false);
	const [hintUsed, setHintUsed] = useState(false);
	const [hintPenalty, setHintPenalty] = useState(0);
	const [hintAppliedToScore, setHintAppliedToScore] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [attempt, setAttempt] = useState(1);
	const [pendingScore, setPendingScore] = useState(0);

	const NUMBERATTEMPTS = 2;
	const CORRECT_ANSWER_POINTS = 10;
	const WRONG_ANSWER_POINTS = -5;
	const CORRECT_ANSWER_POINTS_2 = 3;
	const WRONG_ANSWER_POINTS_2 = 0;

	const retroalimentacion =
		"8,4%. Solo este porcentaje presentó HTA grave (de grado ≥ 3), lo que permite dar seguridad al paciente bajo una monitorización clínica estrecha.";
	const retroalimentacionBlockHtml = buildQuizFeedbackBlockHtml(retroalimentacion, "blue");

	async function submitAnswer(finalScore, correct) {
		const totalTime = getTimer();
		dispatch(updateTime(totalTime, 1));

		try {
			const res = await postAnswer({
				scenario: SCENARIO_ID,
				quiz: QUIZ_ID,
				group: group?._id,
				score: finalScore,
				time: totalTime,
				lastVisited: ROUTES.SCENARIO4_CONTEXT3,
				correct,
			});

			if (res.status === 201) {
				const serverGroup = res.data?.group;
				if (serverGroup) {
					dispatch({ type: "GROUP_SET", payload: serverGroup });
				}
				setIsCompleted(true);
				navigate(ROUTES.SCENARIO4_CONTEXT3);
			}
		} catch (error) {
			const status = error?.response?.status;
			const code = error?.response?.data?.code;

			if (status === 409 && code === "E_DUPLICATE_ANSWER") {
				await modalQuestionResolution({
					correct: false,
					question: "Respuesta ya registrada",
					textHtml: `<div class="txt32 bold" style="color:${BLUE}">Esta pregunta ya se había contestado con anterioridad y no se contabilizará, pueden continuar con el resto del juego.</div>`,
					customConfirmButtonHtml: "Continuar",
					color: "blue",
				});
				navigate(ROUTES.SCENARIO4_CONTEXT3);
				return;
			}

			await modalQuestionResolution({
				correct: false,
				question: "No se pudo guardar",
				textHtml: `<div class="txt24 txt-red bold">Ha ocurrido un problema al registrar tu respuesta. Inténtalo de nuevo</div>`,
				customConfirmButtonHtml: "Aceptar",
				color: "blue",
			});
			setIsSubmitting(false);
		}
	}

	const handleHint = async () => {
		const hint = `<img src=${imgPistaQuiz1} alt="Pista visual" style="width:100%;" />`;
		if (hintUsed) {
			await modalHintShow({ hint, imageHintUsed, color: "blue", big: true });
			return;
		}
		const { used } = await modalHint({ image: imageHint, imageHintUsed, hint, color: "blue", big: true });
		if (used && !hintUsed) {
			setHintUsed(true);
			setHintPenalty(3);
		}
	};

	function checkAnswer() {
		if (parseDecimalAnswer(value) === null) {
			modalQuestionResolution({
				correct: false,
				textHtml: `<img src=${estasseguro} alt="¿Estás seguro?" class='estasseguro' /><div class="text-center txtTitulos mt-3 blue">Debenintroducir un valor numérico (punto o coma como separador decimal)</div>`,
				color: "blue",
			});
			return;
		}

		const ok = isCorrectPercentAnswer(value);

		modalConfirm({ estasseguro, color: "blue" }).then((res) => {
			if (!res.isConfirmed) return;
			setIsSubmitting(true);
			const hintThisAttempt = hintUsed && !hintAppliedToScore ? hintPenalty : 0;

			if (ok) {
				const base = attempt === 1 ? CORRECT_ANSWER_POINTS : CORRECT_ANSWER_POINTS_2;
				const attemptDelta = base - hintThisAttempt;
				const newScore = pendingScore + attemptDelta;
				if (hintUsed && !hintAppliedToScore) setHintAppliedToScore(true);

				modalQuestionResolution({
					correct: true,
					question: "HTA grado ≥3 (TITAN)",
					textHtml: `<img src=${respuestacorrecta} alt="Respuesta correcta" style='max-width:320px' />
                      <div class="txt28 bold pt-4">Ganaron ${attemptDelta} puntos en este intento${hintThisAttempt ? " (-3 pista)" : ""}</div>
                      ${attempt === 2 ? `<div class="txt20 mt-2">Correcto en el segundo intento</div>` : ""}
                      <div class="txt20 mt-2">Puntuación total de la pregunta: <b>${newScore}</b></div>
                      ${retroalimentacionBlockHtml}`,
					customConfirmButtonHtml: "Continuar",
					color: "blue",
				}).then(() => {
					submitAnswer(newScore, true);
				});
				return;
			}

			if (attempt < NUMBERATTEMPTS) {
				const attemptDelta = WRONG_ANSWER_POINTS - hintThisAttempt;
				const newScore = pendingScore + attemptDelta;
				if (hintUsed && !hintAppliedToScore) setHintAppliedToScore(true);

				modalQuestionResolution({
					correct: false,
					question: "HTA grado ≥3 (TITAN)",
					textHtml: `<img src=${respuestaincorrecta} alt="Respuesta incorrecta" style='max-width:320px' />
                      <div class="txt28 bold pt-3">Respuesta incorrecta ${attemptDelta} puntos${hintThisAttempt ? " (incluye -3 por pista)" : ""}</div>
                      <div class="txt22 mt-2">Les queda <b>1</b> intento. Elijan otra respuesta.</div>
                      <div class="txt20 mt-2">Acumulado hasta ahora: <b>${newScore}</b></div>`,
					customConfirmButtonHtml: "Intentar de nuevo",
					color: "blue",
				}).then(() => {
					setPendingScore(newScore);
					setAttempt(attempt + 1);
					setValue("");
					setIsSubmitting(false);
				});
				return;
			}

			const attemptDelta = WRONG_ANSWER_POINTS_2 - hintThisAttempt;
			const newScore = pendingScore + attemptDelta;
			if (hintUsed && !hintAppliedToScore) setHintAppliedToScore(true);

			modalQuestionResolution({
				correct: false,
				question: "HTA grado ≥3 (TITAN)",
				textHtml: `<img src=${respuestaincorrecta} alt="Respuesta incorrecta" style='max-width:320px' />
                    <div class="txt28 bold pt-4">Han agotado los intentos</div>
                    ${hintThisAttempt ? `<div class="txt20 mt-2">Se aplicó -3 por pista en este intento</div>` : ""}
                    <div class="txt20 mt-2">Puntuación total de la pregunta: <b>${newScore}</b></div>
                    ${retroalimentacionBlockHtml}`,
				customConfirmButtonHtml: "Continuar",
				color: "blue",
			}).then(() => {
				submitAnswer(newScore, false);
			});
		});
	}

	const remainingAttempts = NUMBERATTEMPTS - attempt + 1;

	return (
		<div className="h-100 d-flex flex-column">
			<div className="container-fluid px-5 pt-3 pb-5 mb-5 flex-grow-1 bgCircleBlue2 overflow-auto mt-3">
				<h3
					className="txt20 txt-white text-center mb-4"
					style={{ backgroundColor: BLUE, borderRadius: "10px", padding: "18px 20px", lineHeight: 1.45 }}
				>
					Ingresen el valor numérico exacto del porcentaje de pacientes en el brazo de <strong>apalutamida</strong> que
					experimentaron HTA de grado ≥3
				</h3>


				<div className="d-flex justify-content-end mb-3">
					<img src={remainingAttempts === 2 ? dosIntentos : unIntento} className="imgIntentos" alt="" />
				</div>
				<div
					className="d-flex flex-wrap align-items-end justify-content-center justify-content-md-between gap-2 mb-4"
					style={{ maxWidth: "960px", margin: "0 auto" }}
				>
					<p className="txt22 text-center mb-0" style={{ lineHeight: 1.6, flex: "1 1 280px" }}>
						El{" "}
						<input
							type="text"
							inputMode="decimal"
							autoComplete="off"
							aria-label="Porcentaje de pacientes con HTA grado 3 o más"
							className="txt32 bold text-center border-0 border-bottom"
							style={{
								width: "6.5rem",
								maxWidth: "40%",
								borderBottom: `3px solid ${BLUE}`,
								color: BLUE,
								background: "transparent",
								outline: "none",
							}}
							value={value}
							onChange={(e) => setValue(e.target.value)}
							placeholder="?"
							disabled={isCompleted || isSubmitting}
						/>{" "}
						% de los pacientes en el estudio TITAN presentaron HTA de grado ≥3
					</p>
				</div>

				<div className="txt18 text-dark mb-4" style={{ maxWidth: "720px", margin: "0 auto" }}>
					<p className="mb-2">
						<strong>El dígito entero</strong> es el valor de la puntuación de Gleason que define la enfermedad de “alto
						riesgo” según los criterios del estudio LATITUDE.
					</p>
					<p className="mb-0">
						<strong>El dígito decimal</strong> es el número mínimo de metástasis óseas necesarias para clasificar un
						caso como “alto volumen” según los criterios CHAARTED.
					</p>
				</div>

				<div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mt-4 pt-2">
					<div className="position-relative d-inline-block">
						<img
							src={btnPistaBlue}
							className={`btnPista ${hintUsed ? "used" : ""}`}
							onClick={handleHint}
							onKeyDown={(e) => {
								if (e.key === "Enter") void handleHint();
							}}
							alt={hintUsed ? "Pista usada" : "Usar pista"}
							title={hintUsed ? "Pista usada (-3)" : "Usar pista (-3 puntos)"}
							role="button"
							tabIndex={0}
						/>
						{hintUsed && <span className="badge-penalty">-3</span>}
					</div>
					<CheckButton
						text="Comprobar respuesta"
						onClick={checkAnswer}
						disabled={isCompleted || isSubmitting}
						color="blue"
						loading={isSubmitting}
					/>
				</div>
			</div>

			<ScenarioBar activeScenario={activeScenario} />
		</div>
	);
}

export default injectIntl(Scenario4Quiz5);
